import React from 'react';
import { useTranslation } from 'react-i18next';

import { JSONSchema } from '../types';

import styles from './RunInputs.module.scss';

interface RunInputsProps {
  /** Values the run was dispatched with, as reported by the backend. */
  input: Record<string, unknown> | null;
  /** App schema — used only to label the fields. */
  schema: JSONSchema | null;
}

function humanizeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * The parameter values a run was started with, shown next to its status so the
 * user can tell what produced the result — including on a deep link or a row
 * picked from the run history, where the form on screen may hold other values.
 */
export const RunInputs: React.FC<RunInputsProps> = ({ input, schema }) => {
  const { t } = useTranslation();

  const entries = Object.entries(input ?? {});
  if (entries.length === 0) return null;

  const properties = schema?.properties ?? {};

  return (
    <details className={styles.root}>
      <summary className={styles.summary}>{t('Inputs for this run')}</summary>
      <dl className={styles.list}>
        {entries.map(([key, value]) => (
          <div key={key} className={styles.item}>
            {/* A field the app dropped from its schema since the run still has
                a value worth showing — fall back to a readable key. */}
            <dt className={styles.label}>
              {properties[key]?.title || humanizeKey(key)}
            </dt>
            <dd className={styles.value}>{formatValue(value)}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
};
